import { useLazyQuery } from '@apollo/client';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { downloadAsXls } from '@openx/components-deal-prompt/lib/utils/downloadAsXls';
import type { SegmentsListFilters } from '@openx/components-segment/types';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import { ExportSegmentsListDocument } from '../../../graphql/query/segments/ExportSegmentsList.generated';
import { GetSegmentsTotalDocument } from '../../../graphql/query/segments/GetSegmentsTotal.generated';
import { ExportSegmentsDialogContent } from '../components/ExportSegmentsDialogContent';
import { buildSegmentsListQueryVariables } from '../utils/buildSegmentsListQueryVariables';
import { getAppliedSegmentsFiltersDisplay } from '../utils/getAppliedSegmentsFiltersDisplay';

type RegisterExportCriteriaOptions = {
  loading?: boolean;
  totalCount?: number;
};

export const useExportSegmentsDialog = () => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const criteriaRef = useRef<AllTableCriteria<SegmentsListFilters> | null>(null);
  const [isExportDisabled, setIsExportDisabled] = useState(true);

  const {
    open: openConfirmationDialog,
    close: closeConfirmationDialog,
    setLoading,
    toogleConfirmationDisabled,
    refresh,
  } = useConfirmationDialog();

  const [fetchSegmentsTotal] = useLazyQuery(GetSegmentsTotalDocument);
  const [exportSegmentsList] = useLazyQuery(ExportSegmentsListDocument);

  const registerCriteria = useCallback(
    (criteria: AllTableCriteria<SegmentsListFilters>, options?: RegisterExportCriteriaOptions) => {
      criteriaRef.current = criteria;

      if (!options) {
        return;
      }

      if (options.loading) {
        setIsExportDisabled(true);
        return;
      }

      setIsExportDisabled((options.totalCount ?? 0) === 0);
    },
    []
  );

  const getQueryVariables = useCallback(() => {
    const criteria = criteriaRef.current;

    if (!criteria) {
      return null;
    }

    const isOaSuborg = session?.account?.name !== OPEN_AUDIENCE_ORG_NAME;

    return buildSegmentsListQueryVariables({
      criteria,
      accountId: session?.account_id,
      restrictToAccount: isOaSuborg,
    });
  }, [session?.account?.name, session?.account_id]);

  const open = useCallback(() => {
    const criteria = criteriaRef.current;
    const queryVariables = getQueryVariables();

    if (!criteria || !queryVariables) {
      enqueueSnackbar(t('Unable to export segments. Please try again.'), { variant: 'error' });
      return;
    }

    const appliedFilters = getAppliedSegmentsFiltersDisplay(criteria.filters, t);

    toogleConfirmationDisabled(true);

    openConfirmationDialog({
      confirmationButtonColor: 'primary',
      confirmationLabel: t('Export & Download'),
      confirmationDisabled: true,
      content: <ExportSegmentsDialogContent appliedFilters={appliedFilters} segmentsCount={null} countLoading />,
      customContent: true,
      onConfirmed: async () => {
        try {
          setLoading(true);

          const { data } = await exportSegmentsList({
            fetchPolicy: 'no-cache',
            variables: {
              where: queryVariables.exportWhere,
              order_by: queryVariables.order_by,
            },
          });

          const fileData = data?.segmentExcelExport?.fileData;

          if (!fileData) {
            throw new Error('Missing export file data');
          }

          downloadAsXls(fileData, `segments-export-${new Date().toISOString().substring(0, 10)}.xlsx`);
          enqueueSnackbar(t('Segments downloaded successfully.'), { variant: 'success' });
          closeConfirmationDialog();
        } catch {
          enqueueSnackbar(t('Failed to export segments'), { variant: 'error' });
        } finally {
          setLoading(false);
        }
      },
      showExtraBtn: false,
      title: t('Export'),
    });

    void (async () => {
      try {
        const { data } = await fetchSegmentsTotal({
          variables: { where: queryVariables.listWhere },
        });
        const count = data?.segment_aggregate?.aggregate?.count ?? 0;

        refresh(
          <ExportSegmentsDialogContent appliedFilters={appliedFilters} segmentsCount={count} countLoading={false} />
        );
        toogleConfirmationDisabled(count === 0);
      } catch {
        refresh(<ExportSegmentsDialogContent appliedFilters={appliedFilters} segmentsCount={0} countLoading={false} />);
        toogleConfirmationDisabled(true);
        enqueueSnackbar(t('Failed to load segments count'), { variant: 'error' });
      }
    })();
  }, [
    closeConfirmationDialog,
    exportSegmentsList,
    fetchSegmentsTotal,
    getQueryVariables,
    openConfirmationDialog,
    refresh,
    setLoading,
    t,
    toogleConfirmationDisabled,
  ]);

  return {
    open,
    registerCriteria,
    isExportDisabled,
  };
};
